/**
 * Format host-probed video stream metrics from analysis pipeline `_meta.video_debug`.
 */

/**
 * @param {unknown} bytes
 * @returns {string | null}
 */
export function formatFileSizeBytes(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) {
    return null;
  }
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  const digits = i === 0 ? 0 : v < 10 ? 2 : v < 100 ? 1 : 0;
  return `${v.toFixed(digits)} ${units[i]}`;
}

/**
 * @param {Record<string, unknown> | null | undefined} stream
 * @param {unknown} sizeBytes
 */
function formatStreamWithOptionalSize(stream, sizeBytes) {
  const dim = formatVideoStreamMetrics(stream);
  const sz = formatFileSizeBytes(sizeBytes);
  if (dim === '—' && !sz) {
    return '—';
  }
  if (dim === '—') {
    return sz;
  }
  if (!sz) {
    return dim;
  }
  return `${dim} · ${sz}`;
}

export function formatVideoStreamMetrics(stream) {
  if (!stream || stream.width == null || stream.height == null) {
    return '—';
  }
  const w = stream.width;
  const h = stream.height;
  const fpsNum = stream.fps != null ? Number(stream.fps) : NaN;
  const fpsStr = Number.isFinite(fpsNum)
    ? fpsNum % 1 === 0
      ? String(Math.round(fpsNum))
      : fpsNum.toFixed(3).replace(/\.?0+$/, '')
    : '?';
  return `${w}×${h} @ ${fpsStr} fps`;
}

/**
 * @param {Record<string, unknown> | null | undefined} videoDebug
 * @returns {{ label: string, value: string }[]}
 */
export function videoProcessingDebugItems(videoDebug) {
  if (!videoDebug || typeof videoDebug !== 'object') {
    return [];
  }
  const rows = [
    {
      label: 'Original video',
      value: formatStreamWithOptionalSize(
        videoDebug.original,
        videoDebug.original_size_bytes
      ),
    },
  ];
  if (videoDebug.transcode_enabled) {
    let convertedVal = formatStreamWithOptionalSize(
      videoDebug.converted,
      videoDebug.converted_size_bytes
    );
    if (!videoDebug.converted && videoDebug.transcode_succeeded === false) {
      convertedVal = '— (transcode failed, original used)';
    } else if (
      !videoDebug.converted &&
      videoDebug.converted_size_bytes == null
    ) {
      convertedVal = '—';
    }
    rows.push({ label: 'Converted video', value: convertedVal });
  }
  return rows;
}
